import React, { useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useOnboarding } from '../../hooks/useOnboarding';
import BackgroundShapes from '../common/BackgroundShapes';
import SearchSection from '../SearchSection'; // Your existing component
import { SelectedLocation } from '../../types';
import { getPageUrl } from '../../utils/urlHelpers';
import { 
  occupations, 
  languages, 
  cleanlinessOptions, 
  socialLevelOptions, 
  workScheduleOptions, 
  petOptions 
} from '../../constants/formData';

// Onboarding page URL configuration
export const onboardingUrl = getPageUrl('/onboarding');

interface OnboardingPageProps {
  onNavigate: (page: string) => void;
}

const OnboardingPage: React.FC<OnboardingPageProps> = ({ onNavigate }) => {
  const { user, token, updateUser } = useAuth();
  const { step, data, loading, error, updateData, nextStep, prevStep, saveStep, totalSteps } = useOnboarding();
  
  const handleNext = async () => {
    // Validate step 1 has required data
    if (step === 1) {
      if (!data.location || data.location.length === 0) {
        alert('Please select at least one location');
        return;
      }
      if (data.age === '') {
        alert('Please enter your age.');
        return;
      }
      if ((data.age as number) < 18 || (data.age as number) > 100) {
        alert('Age must be between 18 and 100.');
        return;
      }
      if (!data.moveInDate) {
        alert('Please select your move-in timeline');
        return;
      }
      if (!data.budget) {
        alert('Please select your budget range');
        return;
      }
    }

    // Validate step 2
    if (step === 2) {
        if (!data.occupation) {
          alert('Please select your occupation');
          return;
        }
        if (!data.cleanliness) {
          alert('Please select your cleanliness preference');
          return;
        }
        if (!data.socialLevel) {
          alert('Please select your social preference');
          return;
        }
        if (!data.workSchedule) {
          alert('Please select your work schedule');
          return;
        }
    }

    console.log('Current step:', step);
    console.log('Current data:', data);

    if (token) {
      const saved = await saveStep(token);
      console.log('Save step result:', saved);

      if (saved) {
        if (step === totalSteps) {
          console.log('Completing onboarding, navigating to dashboard');
          
          console.log('Before updateUser, current user:', user);
          // Update the user object to reflect completed onboarding
          updateUser({ 
            onboardingCompleted: true,
            onboardingStep: 3,
            profileCompleteness: 100
          });
          console.log('After updateUser called');

          onNavigate('dashboard');
        } else {
          nextStep();
          console.log('nextStep called, new step should be:', step + 1);
        }
      } else {
        console.log('Save step failed');
      }
    } else {
      alert('No authentication token found. Please log in again.');
      onNavigate('quick-signup');
    }
  };

  const handlePrev = () => {
    prevStep();
  };

  // This updates the onboarding data whenever locations change
  const handleLocationChange = useCallback((locations: SelectedLocation[]) => {
    updateData({ location: locations });
  }, [updateData]);

  // Helper for handling pet multi-select with "none" logic
  const handlePetToggle = (currentArray: string[], value: string, field: 'hasPets' | 'okWithPets') => {
    if (value === 'none') {
      // If selecting "none", clear all others and set to ["none"]
      updateData({ [field]: ['none'] });
    } else {
      // If selecting a pet, remove "none" if present and toggle the pet
      const filteredArray = currentArray.filter(item => item !== 'none');
      const newArray = filteredArray.includes(value)
        ? filteredArray.filter(item => item !== value)
        : [...filteredArray, value];
      updateData({ [field]: newArray });
    }
  };

  // Helper for handling regular multi-select (no "none" logic)
  const handleArrayToggle = (currentArray: string[], value: string, updateFn: (newArray: string[]) => void) => {
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    updateFn(newArray);
  };

  const renderStepContent = () => {
    console.log('Rendering step:', step);
    
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-coffee mb-2">Where do you want to live?</h2>
              <p className="text-coffee opacity-80">Select up to 5 neighborhoods you'd consider</p>
            </div>

            {/* SearchSection with real-time updates enabled */}
            <SearchSection 
            onSearch={handleLocationChange}
            showSearchButton={false}
            realTimeUpdate={true}
            initialLocations={data.location || []}
            />

            {/* Visual feedback for selected locations */}
            {data.location && data.location.length > 0 && (
              <div className="bg-green-100 border-2 border-green-500 rounded-lg p-3 text-center">
                <p className="text-green-700 text-sm">
                  ✓ {data.location.length} neighborhood{data.location.length > 1 ? 's' : ''} selected
                </p>
              </div>
            )}

            <hr className="border-coffee border-opacity-20" />

            <div className="space-y-4">
              <div>
                <label className="block text-coffee mb-2 font-semibold">Housing Situation</label>
                <select
                  value={data.housingSituation}
                  onChange={(e) => updateData({ housingSituation: e.target.value as any })}
                  className="w-full p-3 rounded-lg bg-white text-coffee border border-coffee focus:border-terracotta focus:outline-none focus:ring-2 focus:ring-terracotta transition-colors"
                >
                  <option value="" className="text-gray-800">Select your situation</option>
                  <option value="looking" className="text-gray-800">Looking for a place together</option>
                  <option value="have-apartment" className="text-gray-800">I have a place, need roommate</option>
                  <option value="flexible" className="text-gray-800">Flexible with either option</option>
                </select>
              </div>

              <div>
                <label className="block text-coffee mb-2 font-semibold">Budget Range *</label>
                <select
                  value={data.budget || ''}
                  onChange={(e) => updateData({ budget: e.target.value })}
                  className="w-full p-3 rounded-lg bg-white text-coffee border border-coffee focus:border-terracotta focus:outline-none focus:ring-2 focus:ring-terracotta transition-colors"
                >
                  <option value="" className="text-gray-800">Select budget range</option>
                  <option value="1000-1500" className="text-gray-800">$1,000 - $1,500/month</option>
                  <option value="1500-2000" className="text-gray-800">$1,500 - $2,000/month</option>
                  <option value="2000-2500" className="text-gray-800">$2,000 - $2,500/month</option>
                  <option value="2500-3000" className="text-gray-800">$2,500 - $3,000/month</option>
                  <option value="3000+" className="text-gray-800">$3,000+/month</option>
                </select>
              </div>

              <div>
                <label className="block text-coffee mb-2 font-semibold">Move-in Month *</label>
                {(() => {
                  const now = new Date();
                  const currentYear = now.getFullYear();
                  const currentMonth = now.getMonth() + 1; // 1-12
                  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
                  // Parse existing value
                  const existingParts = (data.moveInDate || '').split('-');
                  const selectedYear = existingParts[0] || '';
                  const selectedMonth = existingParts[1] || '';
                  // Year options: 2025 (or earlier if current year is before 2025) through current year + 3
                  const startYear = Math.min(2025, currentYear);
                  const yearOptions = Array.from({ length: currentYear + 3 - startYear + 1 }, (_, i) => startYear + i);
                  // Month options: past years show all months; current year filters to current month onward; future = all
                  const getMonthOptions = (year: number) => {
                    const minMonth = year === currentYear ? currentMonth : 1;
                    return monthNames
                      .map((name, idx) => ({ label: name, value: String(idx + 1).padStart(2, '0') }))
                      .filter((_, idx) => idx + 1 >= minMonth);
                  };
                  const handleYearChange = (year: string) => {
                    if (!year) { updateData({ moveInDate: '' }); return; }
                    const y = parseInt(year);
                    const minM = y === currentYear ? currentMonth : 1;
                    const m = selectedMonth && parseInt(selectedMonth) >= minM ? selectedMonth : String(minM).padStart(2, '0');
                    updateData({ moveInDate: `${year}-${m}-01` });
                  };
                  const handleMonthChange = (month: string) => {
                    if (!month || !selectedYear) return;
                    updateData({ moveInDate: `${selectedYear}-${month}-01` });
                  };
                  return (
                    <div className="flex gap-3">
                      <select
                        value={selectedMonth}
                        onChange={(e) => handleMonthChange(e.target.value)}
                        className="flex-1 p-3 rounded-lg bg-white text-coffee border border-coffee focus:border-terracotta focus:outline-none focus:ring-2 focus:ring-terracotta transition-colors"
                      >
                        <option value="" className="text-gray-800">Month</option>
                        {getMonthOptions(parseInt(selectedYear) || currentYear).map(m => (
                          <option key={m.value} value={m.value} className="text-gray-800">{m.label}</option>
                        ))}
                      </select>
                      <select
                        value={selectedYear}
                        onChange={(e) => handleYearChange(e.target.value)}
                        className="w-32 p-3 rounded-lg bg-white text-coffee border border-coffee focus:border-terracotta focus:outline-none focus:ring-2 focus:ring-terracotta transition-colors"
                      >
                        <option value="" className="text-gray-800">Year</option>
                        {yearOptions.map(y => (
                          <option key={y} value={String(y)} className="text-gray-800">{y}</option>
                        ))}
                      </select>
                    </div>
                  );
                })()}
              </div>

              <div>
                <label className="block text-coffee mb-2 font-semibold">Age *</label>
                <p className="text-coffee opacity-70 text-sm mb-2">Must be 18–100</p>
                <input
                  type="number"
                  min={18}
                  max={100}
                  value={data.age === '' ? '' : data.age}
                  onChange={(e) => {
                    const val = e.target.value;
                    updateData({ age: val === '' ? '' : parseInt(val, 10) });
                  }}
                  placeholder="Enter your age"
                  className="w-full p-3 rounded-lg bg-white text-coffee placeholder-coffee placeholder-opacity-50 border border-coffee focus:border-terracotta focus:outline-none focus:ring-2 focus:ring-terracotta transition-colors"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-coffee mb-2">Lifestyle Preferences</h2>
              <p className="text-coffee opacity-80">Help us find your most compatible roommate</p>
            </div>

            <div className="space-y-6">
              {/* 1. Occupation - FIRST */}
              <div>
                <label className="block text-coffee mb-3 font-semibold">What's your occupation? *</label>
                <select
                  value={data.occupation}
                  onChange={(e) => updateData({ occupation: e.target.value })}
                  className="w-full p-3 rounded-lg bg-white text-coffee border border-coffee focus:border-terracotta focus:outline-none focus:ring-2 focus:ring-terracotta transition-colors"
                >
                  {occupations.map((option) => (
                    <option key={option.value} value={option.value} className="text-gray-800">
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* 2. Languages */}
              <div>
                <label className="block text-coffee mb-3 font-semibold">What languages do you speak? (Select all that apply)</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {languages.map((lang) => (
                    <button
                      key={lang.value}
                      onClick={() => handleArrayToggle(
                        data.languages,
                        lang.value,
                        (newArray) => updateData({ languages: newArray })
                      )}
                      className={`p-2 rounded-lg text-left transition-colors border-2 ${
                        data.languages.includes(lang.value)
                          ? 'bg-terracotta border-terracotta text-white'
                          : 'bg-white border-coffee text-coffee hover:bg-cream'
                      } text-sm`}
                    >
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* 3. Cleanliness */}
              <div>
                <label className="block text-coffee mb-3 font-semibold">How important is cleanliness to you? *</label>
                <div className="grid grid-cols-1 gap-3">
                  {cleanlinessOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => updateData({ cleanliness: option.value })}
                      className={`p-3 rounded-lg text-left transition-colors border-2 ${
                        data.cleanliness === option.value
                          ? 'bg-terracotta border-terracotta text-white'
                          : 'bg-white border-coffee text-coffee hover:bg-cream'
                      }`}
                    >
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* 4. Social Level */}
              <div>
                <label className="block text-coffee mb-3 font-semibold">What's your social preference? *</label>
                <div className="grid grid-cols-1 gap-3">
                  {socialLevelOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => updateData({ socialLevel: option.value })}
                      className={`p-3 rounded-lg text-left transition-colors border-2 ${
                        data.socialLevel === option.value
                          ? 'bg-terracotta border-terracotta text-white'
                          : 'bg-white border-coffee text-coffee hover:bg-cream'
                      }`}
                    >
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* 5. Work Schedule */}
              <div>
                <label className="block text-coffee mb-3 font-semibold">What's your work schedule like? *</label>
                <div className="grid grid-cols-1 gap-3">
                  {workScheduleOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => updateData({ workSchedule: option.value })}
                      className={`p-3 rounded-lg text-left transition-colors border-2 ${
                        data.workSchedule === option.value
                          ? 'bg-terracotta border-terracotta text-white'
                          : 'bg-white border-coffee text-coffee hover:bg-cream'
                      }`}
                    >
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* 6. Do you have pets? */}
              <div>
                <label className="block text-coffee mb-3 font-semibold">Do you have pets? (Select all that apply)</label>
                <div className="grid grid-cols-4 gap-3">
                  {petOptions.map((pet) => (
                    <button
                      key={pet.value}
                      onClick={() => handlePetToggle(data.hasPets, pet.value, 'hasPets')}
                      className={`p-3 rounded-lg text-center transition-colors border-2 ${
                        data.hasPets.includes(pet.value)
                          ? 'bg-steel border-steel text-white'
                          : 'bg-white border-coffee text-coffee hover:bg-cream'
                      }`}
                    >
                      <span>{pet.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* 7. OK with roommate's pets? */}
              <div>
                <label className="block text-coffee mb-3 font-semibold">What roommate's pets are you okay with? (Select all that apply)</label>
                <div className="grid grid-cols-4 gap-3">
                  {petOptions.map((pet) => (
                    <button
                      key={pet.value}
                      onClick={() => handlePetToggle(data.okWithPets, pet.value, 'okWithPets')}
                      className={`p-3 rounded-lg text-center transition-colors border-2 ${
                        data.okWithPets.includes(pet.value)
                          ? 'bg-terracotta border-terracotta text-white'
                          : 'bg-white border-coffee text-coffee hover:bg-cream'
                      }`}
                    >
                      <span>{pet.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* 8. About Me (optional) */}
              <hr className="border-coffee border-opacity-20" />
              <div>
                <label className="block text-coffee mb-3 font-semibold">About Me (optional)</label>
                <p className="text-coffee opacity-80 text-sm mb-2">Add a short bio for your profile</p>
                <textarea
                  value={data.bio || ''}
                  onChange={(e) => updateData({ bio: e.target.value.slice(0, 500) })}
                  placeholder="What should potential roommates know about you?"
                  className="w-full p-3 rounded-lg bg-white text-coffee placeholder-coffee placeholder-opacity-50 border border-coffee focus:border-terracotta focus:outline-none focus:ring-2 focus:ring-terracotta transition-colors min-h-[100px]"
                  maxLength={500}
                  disabled={loading}
                />
                <p className="text-coffee opacity-60 text-xs mt-1">{(data.bio || '').length} / 500</p>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-coffee mb-2">Review Your Profile</h2>
                <p className="text-coffee opacity-80">Make sure everything looks good before we find your matches</p>
              </div>
  
              <div className="bg-white border border-coffee rounded-lg p-6 space-y-6">
                {/* Housing Preferences - FIRST */}
              <div>
                <h3 className="text-coffee font-semibold mb-3 text-lg">🏠 Housing Preferences</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-coffee font-semibold mb-1">Selected Areas</h4>
                    <div className="flex flex-wrap gap-2">
                      {data.location.map((location, index) => (
                        <span key={location.id} className="bg-white border border-coffee text-coffee px-2 py-1 rounded-full text-xs">
                          {location.borough} - {location.neighborhood}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-coffee font-semibold mb-1">Budget</h4>
                    <p className="text-coffee opacity-80 text-sm">${data.budget}/month</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <h4 className="text-coffee font-semibold mb-1">Housing Situation</h4>
                    <p className="text-coffee opacity-80 text-sm">
                      {data.housingSituation === 'looking' && 'Looking for a place together'}
                      {data.housingSituation === 'have-apartment' && 'I have a place, need roommate'}
                      {data.housingSituation === 'flexible' && 'Flexible with either option'}
                      {!data.housingSituation && 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-coffee font-semibold mb-1">Move-in Month</h4>
                    <p className="text-coffee opacity-80 text-sm">
                      {data.moveInDate
                        ? (() => {
                            const d = new Date(data.moveInDate + 'T00:00:00');
                            return isNaN(d.getTime())
                              ? data.moveInDate
                              : d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                          })()
                        : 'Not specified'}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <h4 className="text-coffee font-semibold mb-1">Age</h4>
                    <p className="text-coffee opacity-80 text-sm">
                      {data.age !== '' ? data.age : 'Not specified'}
                    </p>
                  </div>
                </div>
              </div>
  
                <hr className="border-coffee border-opacity-20" />
  
                {/* Individual Fields - NO NESTING */}
              <div>
                <h3 className="text-coffee font-semibold mb-2">💼 Occupation</h3>
                <p className="text-coffee opacity-80 text-sm">
                  {occupations.find(o => o.value === data.occupation)?.label || 'Not specified'}
                </p>
              </div>

              <div>
                <h3 className="text-coffee font-semibold mb-2">🌐 Languages</h3>
                <div className="flex flex-wrap gap-2">
                  {data.languages.length > 0 ? (
                    data.languages.map((lang) => (
                      <span key={lang} className="bg-white border border-coffee text-coffee px-2 py-1 rounded-full text-xs">
                        {languages.find(l => l.value === lang)?.label || lang}
                      </span>
                    ))
                  ) : (
                    <p className="text-coffee opacity-80 text-sm">Not specified</p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-coffee font-semibold mb-2">🧹 Cleanliness</h3>
                <p className="text-coffee opacity-80 text-sm">
                  {cleanlinessOptions.find(o => o.value === data.cleanliness)?.label || 'Not specified'}
                </p>
              </div>

              <div>
                <h3 className="text-coffee font-semibold mb-2">👥 Social Level</h3>
                <p className="text-coffee opacity-80 text-sm">
                  {socialLevelOptions.find(o => o.value === data.socialLevel)?.label || 'Not specified'}
                </p>
              </div>

              <div>
                <h3 className="text-coffee font-semibold mb-2">⏰ Work Schedule</h3>
                <p className="text-coffee opacity-80 text-sm">
                  {workScheduleOptions.find(o => o.value === data.workSchedule)?.label || 'Not specified'}
                </p>
              </div>

              <div>
                <h3 className="text-coffee font-semibold mb-2">🐕 Has Pets</h3>
                <div className="flex flex-wrap gap-2">
                  {data.hasPets.length > 0 ? (
                    data.hasPets.map((pet) => (
                      <span key={pet} className="bg-steel border-steel text-white px-2 py-1 rounded-full text-xs">
                        {petOptions.find(p => p.value === pet)?.label || pet}
                      </span>
                    ))
                  ) : (
                    <p className="text-coffee opacity-80 text-sm">Not specified</p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-coffee font-semibold mb-2">🐾 OK with Pets</h3>
                <div className="flex flex-wrap gap-2">
                  {data.okWithPets.length > 0 ? (
                    data.okWithPets.map((pet) => (
                      <span key={pet} className="bg-terracotta border-terracotta text-white px-2 py-1 rounded-full text-xs">
                        {petOptions.find(p => p.value === pet)?.label || pet}
                      </span>
                    ))
                  ) : (
                    <p className="text-coffee opacity-80 text-sm">Not specified</p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-coffee font-semibold mb-2">📝 About Me</h3>
                {data.bio && data.bio.trim() ? (
                  <p className="text-coffee opacity-80 text-sm whitespace-pre-wrap">{data.bio}</p>
                ) : (
                  <p className="text-coffee opacity-60 text-sm italic">No bio added</p>
                )}
              </div>
            </div>
  
              <div className="bg-terracotta bg-opacity-20 border-2 border-terracotta rounded-lg p-4 text-center">
                <p className="text-coffee">
                  🎉 Ready to find your perfect roommate!
                </p>
              </div>
            </div>  
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-paper relative">
      <BackgroundShapes />
      
      <div className="container mx-auto px-4 py-8 relative z-10">
        <div className="max-w-2xl mx-auto">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-2xl font-bold text-coffee">Complete Your Profile</h1>
              <span className="text-coffee opacity-80">Step {step} of {totalSteps}</span>
            </div>
            <div className="w-full bg-white border border-coffee rounded-full h-2">
              <div 
                className="bg-terracotta h-2 rounded-full transition-all duration-300"
                style={{ width: `${(step / totalSteps) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 border-2 border-red-400 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Step Content */}
          <div className="bg-cream border-2 border-coffee rounded-xl p-8 shadow-md mb-8">
            {renderStepContent()}
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <button
              onClick={handlePrev}
              disabled={step === 1}
              className="bg-white border-2 border-coffee text-coffee px-6 py-3 rounded-lg hover:bg-cream transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              ← Previous
            </button>
            
            <button
              onClick={handleNext}
              disabled={loading}
              className="bg-terracotta text-white px-6 py-3 rounded-lg font-bold hover:bg-[#d89050] transition-all duration-300 disabled:opacity-50 border-2 border-coffee shadow-lg"
            >
              {loading ? 'Saving...' : step === totalSteps ? '🎉 Complete Profile' : 'Next →'}
            </button>
          </div>

          {/* Welcome Message */}
          {user && (
            <div className="text-center mt-6">
              <p className="text-coffee opacity-80">Welcome, {user.firstName}! Let's find your perfect roommate.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;